import { useState } from 'react';

import { calculateDrawdownTotalInterest } from '@api/repaymentScheduleSelector';
import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { UKOnly } from '@iwIntlProvider/CountryWrappers';
import { IconAngleRight, IconCalendarOutline } from '@iwoca/icons';
import { Link, Modal, Pill } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import styles from './LoanSummary.module.css';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import LoadingCalculationValue from '../../../../multiLoanDashboard/dashboardComponents/loadingDashboardValue/LoadingDashboardValue';
import { MoneyWithDecimals } from '../../../../ui/moneyWithDecimals';
import { VariableRate } from '../../../../ui/rate';
import SummaryRow from '../../../../ui/summaryRow/SummaryRow';
import {
    getInterestOnlySchedule,
    getNonInterestOnlyPaymentAmount
} from '../../../MyOffers.helpers';
import {
    DrawdownRepaymentSchedule,
    FormalOffer
} from '../../../MyOffers.types';
import RepaymentScheduleModal from '../../repaymentScheduleModal/RepaymentScheduleModal';
import { useGetDiscountedPrice } from '../../useGetDiscountedPrice';
import { InterestOnlyPaymentsAccordion } from '../interestOnly/InterestOnlyPaymentsAccordion';
import WithdrawalFee from '../withdrawalFee/WithdrawalFee';

type LoanSummaryProps = {
    drawdownAmount: number;
    selectedOffer: FormalOffer | null | undefined;
    isAccessUser?: boolean;
};

export const LoanSummary = ({
    drawdownAmount,
    selectedOffer,
    isAccessUser = false
}: LoanSummaryProps) => {
    const stateKey = useStateKey();

    const { hasDiscountedPricing } = useGetDiscountedPrice({
        enabled: getCountry() === 'DE',
        formalOffer: selectedOffer
    });

    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleModalClose = () => {
        setIsModalOpen(!isModalOpen);
    };

    const { isLoading, data: drawdownSchedule } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            selectedOffer?.offer_id,
            selectedOffer?.product_type
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${selectedOffer?.product_type}/${selectedOffer?.offer_id}/?amount=${drawdownAmount}`
            );

            return response.json();
        },
        enabled: Boolean(selectedOffer) && Boolean(drawdownAmount)
    });

    const greyedTextClassName = !selectedOffer && styles.greyText;

    let loanDuration = 12;
    let withdrawalFee = 0;
    let interestRate = 0;

    if (selectedOffer) {
        loanDuration = getDurationInMonths(selectedOffer.duration!);
        withdrawalFee = selectedOffer.withdrawal_fee_percentage || 0;
        interestRate = selectedOffer.interest_rates![0].rate!;
    }

    let repaymentSchedule: DrawdownRepaymentSchedule['schedule'] | null = null;
    let scheduleSummary: DrawdownRepaymentSchedule['summary'];
    let equalMonthlyAmount = 0;
    let totalInterest = 0;
    let totalBalance = 0;
    if (drawdownSchedule?.data) {
        repaymentSchedule = drawdownSchedule.data.schedule;
        scheduleSummary = drawdownSchedule.data.summary;
        // We assume that the !is_equal_repayment means the schedule is for interest-only
        equalMonthlyAmount = scheduleSummary.is_equal_repayment
            ? scheduleSummary.simplified_schedule!.equal_repayments!.amount
            : getNonInterestOnlyPaymentAmount(repaymentSchedule!);
        totalInterest = calculateDrawdownTotalInterest(repaymentSchedule!);
        totalBalance = scheduleSummary!.total_payable!;
    }

    return (
        <div className={classNames(styles.loanSummary, 'w-full')}>
            <div
                className={classNames(
                    styles.loanSummaryTitle,
                    greyedTextClassName
                )}
                data-testid="loan-summary-title"
            >
                <FormattedMessage
                    id="_.draw_down.choose_amount.your_repayments_over_months"
                    defaultMessage={`{isAccessUser, select,
        true {Repayments}
        false {Your repayments}
        other {}
        } over {months} months`}
                    values={{ months: loanDuration, isAccessUser }}
                />

                {selectedOffer && getIsInterestOnlyOffer(selectedOffer) && (
                    <div className={styles.loanSummarySubtitle}>
                        <FormattedMessage
                            id="_.draw_down.choose_amount.interest_only_subtitle"
                            defaultMessage="(includes 3 months interest-only)"
                        />
                    </div>
                )}
            </div>
            <WithdrawalFee
                withdrawalFee={withdrawalFee}
                className={classNames(greyedTextClassName)}
            />
            {!isLoading && hasDiscountedPricing && (
                <div className={styles.discountPill}>
                    <Pill>
                        <FormattedMessage
                            id="_.draw_down_.discountedOffer"
                            defaultMessage="Our offer for you:"
                        />
                    </Pill>
                </div>
            )}
            <SummaryRow
                data-testid="interest-per-30-days"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage
                    id="_.draw_down.choose_amount.interest_rate_label"
                    defaultMessage="Interest rate"
                />
                <LoadingCalculationValue isLoading={isLoading}>
                    <>
                        <UKOnly>
                            <span className={styles.subText}>
                                <FormattedMessage
                                    id="_.draw_down.choose_amount.interest_rate_period"
                                    defaultMessage="per 30 days"
                                />
                            </span>
                        </UKOnly>

                        {!isLoading && hasDiscountedPricing && (
                            <>
                                <span className={styles.strikethrough}>
                                    2,50&nbsp;%
                                </span>{' '}
                            </>
                        )}

                        <VariableRate
                            minimumFractionDigits={2}
                            maximumFractionDigits={3}
                            value={interestRate}
                        />
                    </>
                </LoadingCalculationValue>
            </SummaryRow>
            {selectedOffer && getIsInterestOnlyOffer(selectedOffer) && (
                <InterestOnlyPaymentsAccordion
                    interestOnlyPeriod={
                        getFormalOfferInterestOnlyPeriod(selectedOffer)!
                    }
                    interestOnlySchedule={getInterestOnlySchedule(
                        repaymentSchedule
                    )}
                />
            )}
            <SummaryRow
                data-testid="minimum-monthly-repayments"
                className={classNames(greyedTextClassName)}
            >
                <span className={styles.wrapText}>
                    {selectedOffer && getIsInterestOnlyOffer(selectedOffer) ? (
                        <FormattedMessage
                            id="_.draw_down.choose_amount.interest_only_monthly_repayment"
                            defaultMessage="Minimum monthly repayment (months {monthlyPaymentStart} - {monthlyPaymentEnd})"
                            values={{
                                monthlyPaymentStart:
                                    getFormalOfferInterestOnlyPeriod(
                                        selectedOffer
                                    )! + 1,
                                monthlyPaymentEnd: getDurationInMonths(
                                    selectedOffer.duration!
                                )
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.draw_down.choose_amount.monthly_repayment"
                            defaultMessage="Minimum monthly repayment"
                        />
                    )}
                </span>
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={equalMonthlyAmount}
                    />
                </LoadingCalculationValue>
            </SummaryRow>
            <SummaryRow
                data-testid="total-interest"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage
                    id="_.draw_down.choose_amount.total_interest"
                    defaultMessage="Total interest"
                />
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={totalInterest}
                    />
                </LoadingCalculationValue>
            </SummaryRow>
            <SummaryRow
                data-testid="total-repayment-amount"
                className={classNames(greyedTextClassName)}
            >
                <FormattedMessage
                    id="_.draw_down.choose_amount.total_repayment"
                    defaultMessage="Total repayment"
                />
                <LoadingCalculationValue isLoading={isLoading}>
                    <MoneyWithDecimals
                        className={styles.summaryAmount}
                        value={totalBalance}
                    />
                </LoadingCalculationValue>
            </SummaryRow>
            {selectedOffer && !isAccessUser && (
                <Link
                    className={styles.showScheduleLink}
                    onClick={() => {
                        setIsModalOpen(true);
                    }}
                >
                    <IconCalendarOutline />
                    <FormattedMessage
                        id="_.draw_down.choose_amount.my_repayment_schedule"
                        defaultMessage="See my repayment schedule"
                    />
                    <IconAngleRight />
                </Link>
            )}
            <Modal isOpen={isModalOpen} onClose={handleModalClose} closeIcon>
                <RepaymentScheduleModal
                    offerId={selectedOffer?.offer_id!}
                    drawdownAmount={drawdownAmount}
                />
            </Modal>
        </div>
    );
};
